import { useCallback, useEffect, useState } from 'react';
import {
  availabilityApi,
  type AvailabilityGroup,
  type AvailabilityGroupPeriod,
  type AvailabilityScopeRule,
} from '@/lib/availability';

export function useAvailabilityGroups() {
  const [groups, setGroups] = useState<AvailabilityGroup[]>([]);
  const [scopeRules, setScopeRules] = useState<AvailabilityScopeRule[]>([]);
  const [periods, setPeriods] = useState<AvailabilityGroupPeriod[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, r] = await Promise.all([
        availabilityApi.listGroups(),
        availabilityApi.listScopeRules(),
      ]);
      setGroups(g);
      setScopeRules(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPeriods = useCallback(async (groupId: number) => {
    try {
      const p = await availabilityApi.listPeriods(groupId);
      setPeriods(p);
    } catch {
      setPeriods([]);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (selectedGroupId) {
      loadPeriods(selectedGroupId);
    } else {
      setPeriods([]);
    }
  }, [selectedGroupId, loadPeriods]);

  return {
    groups,
    scopeRules,
    periods,
    selectedGroupId,
    setSelectedGroupId,
    loading,
    error,
    reload,
    loadPeriods,
    setScopeRules,
    setPeriods,
    setGroups,
  };
}
